'use client'

import { Icon } from '@iconify/react'
import { Button } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'

export interface ConnectItem {
  icon: string
  title: string
  description: string
  /** Connected state — `rows` layout shows Connect vs Disconnect accordingly */
  connected?: boolean
  /** Set to true for monochrome icons that need inversion in dark mode */
  darkModeInvert?: boolean
}

export interface ConnectProps {
  title?: string
  description?: string
  items?: ConnectItem[]
  /**
   * Layout:
   * - `grid` (default): 4-column overview cards, no action — an "available apps" wall
   * - `rows`: vertical rows each with a Connect/Disconnect button — the account-connection flow
   */
  layout?: 'grid' | 'rows'
  /** Connect/disconnect callback (`rows` layout) */
  onConnect?: (item: ConnectItem) => void
  className?: string
}

const defaultItems: ConnectItem[] = [
  { icon: 'logos:github-icon', title: 'GitHub', description: 'Automate workflows, sync repos, and track changes in real-time.', darkModeInvert: true },
  { icon: 'logos:slack-icon', title: 'Slack', description: 'Get notifications and updates directly in your Slack channels.' },
  { icon: 'logos:notion-icon', title: 'Notion', description: 'Sync documentation and keep your team aligned automatically.', darkModeInvert: true },
  { icon: 'logos:linear-icon', title: 'Linear', description: 'Connect issues and projects for streamlined development workflows.' },
  { icon: 'logos:vercel-icon', title: 'Vercel', description: 'Deploy automatically and preview changes before they go live.', darkModeInvert: true },
  { icon: 'logos:supabase-icon', title: 'Supabase', description: 'Sync your database and manage authentication seamlessly.' },
  { icon: 'logos:stripe', title: 'Stripe', description: 'Process payments and manage subscriptions effortlessly.' },
  { icon: 'logos:openai-icon', title: 'OpenAI', description: 'Power your app with AI capabilities and intelligent automation.', darkModeInvert: true },
]

function ItemIcon({ item, size }: { item: ConnectItem; size: number }) {
  const isUrl = item.icon.startsWith('http') || item.icon.startsWith('/')
  const className = item.darkModeInvert ? 'dark:invert' : undefined
  return isUrl ? (
    <img src={item.icon} alt={item.title} width={size} height={size} className={className} />
  ) : (
    <Icon icon={item.icon} width={size} height={size} className={className} />
  )
}

/**
 * Connect - App / account connection directory
 *
 * Prop-driven layout: `grid` (overview cards) or `rows` (connect flow with
 * per-row Connect/Disconnect buttons). Built for in-product account/app
 * connection flows (e.g. Zapier/Composio), not landing-page marketing — for a
 * "works with" logo wall on a landing page use `LogosSection`.
 */
export function Connect({
  title = 'Integrations',
  description = 'Connect your favourite apps to your workflow.',
  items = defaultItems,
  layout = 'grid',
  onConnect,
  className,
}: ConnectProps) {
  return (
    <section className={cn('py-32', className)}>
      <div className="container mx-auto">
        <div className="mx-auto mb-12 flex max-w-5xl flex-col items-center text-center">
          <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">{title}</h2>
          <div className="mt-4 max-w-3xl"><Markdown size="base">{description}</Markdown></div>
        </div>

        {layout === 'rows' ? (
          <div className="mx-auto max-w-2xl space-y-4">
            {items.map((item) => (
              <div
                key={item.title}
                className="flex flex-col items-start justify-between rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-background px-6 py-4 shadow-sm md:flex-row md:items-center"
              >
                <div className="mr-0 flex w-full flex-col items-start gap-4 md:mr-4 md:w-auto md:flex-row md:items-center">
                  <div className="flex size-10 items-center justify-center">
                    <ItemIcon item={item} size={40} />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 md:ml-4">
                  <Button
                    variant={item.connected ? 'secondary' : 'outline'}
                    onClick={() => onConnect?.(item)}
                  >
                    {item.connected ? 'Disconnect' : 'Connect'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
              <div
                key={item.title}
                className="flex min-h-[140px] flex-col items-start rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-background p-6 shadow-sm transition hover:shadow-md"
              >
                <div className="mb-4 flex size-10 items-center justify-center rounded-[var(--radius-md,calc(var(--radius)*0.75))] bg-muted">
                  <ItemIcon item={item} size={24} />
                </div>
                <div className="mb-1 text-base font-medium">{item.title}</div>
                <div className="text-xs leading-snug text-muted-foreground">{item.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
